import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { isTabletMaxWidth } from '@/helpers';
import styles from './alertpanel.scss?inline';

/**
 * Floating alert panel with header / content / footer slots.
 *
 * @slot header - title / custom header content
 * @slot content - collapsible body
 * @slot footer - footer actions
 *
 * @fires close - dismiss button clicked
 * @fires expand-change - expanded state changed (detail.expanded)
 */
export default class SotbitAlertPanel extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        open: { type: Boolean, reflect: true },
        expanded: { type: Boolean, reflect: true },
        expandIcon: { type: String, attribute: 'expand-icon' },
        collapseIcon: { type: String, attribute: 'collapse-icon' },
        closeIcon: { type: String, attribute: 'close-icon' },
        hideToggle: { type: Boolean, attribute: 'hide-toggle' },
        hideClose: { type: Boolean, attribute: 'hide-close' },
        _hasFooter: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.open = false;
        this.expanded = false;
        this.expandIcon = 'chevron-up';
        this.collapseIcon = 'chevron-down';
        this.closeIcon = 'close';
        this.hideToggle = false;
        this.hideClose = false;
        this._hasFooter = false;
        this._overlayRef = createRef();
        this._onResize = () => this._syncMobileOverlay();
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this._onResize);
        this._overlayRef.value?.hide();
        super.disconnectedCallback();
    }

    updated(changedProperties) {
        if (changedProperties.has('open') || changedProperties.has('expanded')) {
            this._syncMobileOverlay();
        }
    }

    show() {
        if (this.open) {
            return;
        }

        // Paint closed state first so enter transition (slide up) can run.
        this.open = false;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.open = true;
            });
        });
    }

    hide() {
        this.open = false;
        this.expanded = false;
    }

    toggleExpand(force) {
        this.expanded = typeof force === 'boolean' ? force : !this.expanded;
        this.dispatchEvent(
            new CustomEvent('expand-change', {
                bubbles: true,
                composed: true,
                detail: { expanded: this.expanded },
            })
        );
    }

    _syncMobileOverlay() {
        const overlay = this._overlayRef.value;
        if (!overlay) {
            return;
        }

        const shouldShow = this.open && this.expanded && isTabletMaxWidth();
        if (shouldShow) {
            overlay.show();

            return;
        }

        if (overlay.isShow || overlay.hasAttribute('show')) {
            overlay.hide();
        }
    }

    _handleOverlayClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleExpand(false);
    }

    _handleToggle(event) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleExpand();
    }

    _handleClose(event) {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
        this.dispatchEvent(
            new CustomEvent('close', {
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleFooterSlotChange(event) {
        this._hasFooter = event.target.assignedNodes({ flatten: true }).some((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                return true;
            }

            return node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim());
        });
    }

    render() {
        return html`
            <sotbit-overlay
                class="overlay"
                part="overlay"
                fixed
                ${ref(this._overlayRef)}
                @click="${this._handleOverlayClick}"
            ></sotbit-overlay>
            <div class="panel" part="panel">
                <div class="header" part="header">
                    <div class="header__content" part="header-content">
                        <slot name="header"></slot>
                    </div>
                    <div class="header__controls" part="controls">
                        ${this.hideToggle
                            ? nothing
                            : html`
                                  <sotbit-button-icon
                                      class="toggle"
                                      part="toggle"
                                      size="s"
                                      background
                                      @click="${this._handleToggle}"
                                  >
                                      <sotbit-icon
                                          slot="content"
                                          variant="secondary"
                                          icon="${this.expanded ? this.collapseIcon : this.expandIcon}"
                                          no-padding
                                      ></sotbit-icon>
                                  </sotbit-button-icon>
                              `}
                        ${this.hideClose
                            ? nothing
                            : html`
                                  <sotbit-button-icon
                                      class="close"
                                      part="close"
                                      size="s"
                                      background
                                      @click="${this._handleClose}"
                                  >
                                      <sotbit-icon
                                          slot="content"
                                          variant="secondary"
                                          icon="${this.closeIcon}"
                                          no-padding
                                      ></sotbit-icon>
                                  </sotbit-button-icon>
                              `}
                    </div>
                </div>
                <div class="body" part="body">
                    <div class="body__inner" part="body-inner">
                        <div class="content" part="content">
                            <sotbit-scroll-panel class="content-scroll" part="content-scroll">
                                <slot name="content"></slot>
                            </sotbit-scroll-panel>
                        </div>
                        <div class="footer ${this._hasFooter ? '' : 'hidden'}" part="footer">
                            <slot name="footer" @slotchange="${this._handleFooterSlotChange}"></slot>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}
